import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Phone } from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import { useToast } from '@/shared/ui/Toast';
import { strategyApi } from '../api/strategyApi';
import { strategyKeys } from '../queryKeys';
import { Button, Card, SectionLabel } from '@/shared/ui';
import { brandQuestionsFor, type Pick } from '../constants/brandQuestions';
import { useVertical } from '@/shared/hooks/useVertical';
import styles from './ConfirmCallStep.module.css';

// ── Component ─────────────────────────────────────────────────────────────────

export function ConfirmCallStep() {
  const { verticalId } = useVertical();
  const brandQuestions = brandQuestionsFor(verticalId);
  const { currentLocation, locations, setCurrentLocation } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const confirmToken = searchParams.get('confirmToken');

  // ── Load session ───────────────────────────────────────────────────────────

  const tokenQuery = useQuery({
    queryKey: strategyKeys.vapiToken(confirmToken ?? ''),
    queryFn: () => strategyApi.getSessionByConfirmToken(confirmToken!),
    enabled: !!confirmToken,
  });

  // The emailed link names its location. An owner with several locations may
  // open it while another one is selected: everything on this page (answers,
  // confirm) must act on the link's location, so switch to it.
  const tokenLocationId = tokenQuery.data?.data?.locationId;
  const locationId = tokenLocationId ?? currentLocation?.id ?? '';
  useEffect(() => {
    if (!tokenLocationId || currentLocation?.id === tokenLocationId) return;
    const match = locations.find((l) => l.id === tokenLocationId);
    if (match) setCurrentLocation(match);
  }, [tokenLocationId, currentLocation?.id, locations, setCurrentLocation]);

  const sessionQuery = useQuery({
    queryKey: strategyKeys.vapiSession(locationId),
    queryFn: () => strategyApi.getVapiSession(locationId),
    enabled: !!locationId && !confirmToken,
  });

  // ── Load existing responses to pre-fill archetype picks ────────────────────

  const responsesQuery = useQuery({
    queryKey: strategyKeys.responses(locationId),
    queryFn: () => strategyApi.getResponses(locationId),
    enabled: !!locationId,
  });

  const savedResponses = responsesQuery.data?.data ?? [];

  const session = confirmToken ? tokenQuery.data?.data : sessionQuery.data?.data;
  const fieldStatus = session?.fieldStatus ?? {};
  const captureLevel = session?.captureLevel;
  const tokenExpired = confirmToken
    ? (tokenQuery.data?.data as { tokenExpired?: boolean })?.tokenExpired
    : false;

  // ── Editable picks for missing/unclear fields ──────────────────────────────

  const [picks, setPicks] = useState<Pick[]>(Array(8).fill(null));
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized || !responsesQuery.data) return;
    const init = brandQuestions.map(({ questionKey: key }) => {
      const r = savedResponses.find((res) => res.question.questionKey === key);
      if (!r) return null;
      const v = r.response;
      return v === 0 || v === 1 ? (v as 0 | 1) : null;
    });
    setPicks(init as Pick[]);
    setInitialized(true);
  }, [initialized, responsesQuery.data, savedResponses]);

  // ── Resend email ───────────────────────────────────────────────────────────

  const { mutate: resendEmail, isPending: isResending } = useMutation({
    mutationFn: () => strategyApi.resendConfirmEmail(locationId),
    onSuccess: () => showToast('New confirm link sent to your email.', 'success'),
    onError: (err: unknown) => showToast((err as Error).message ?? 'Failed to resend', 'error'),
  });

  // ── Save picks for missing fields before confirming ────────────────────────

  const questionsQuery = useQuery({
    queryKey: strategyKeys.questions(locationId),
    queryFn: () => strategyApi.getQuestions(locationId),
    enabled: !!locationId,
  });
  const questions = questionsQuery.data?.data ?? [];

  const { mutate: savePicks, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      // Save every pick that differs from what's stored — including changes
      // to answers the call captured, not only the gaps.
      const updates: Array<{ questionId: string; response: unknown }> = [];
      brandQuestions.forEach(({ questionKey: key }, i) => {
        const pick = picks[i];
        if (pick === null) return;
        const saved = savedResponses.find((r) => r.question.questionKey === key)?.response;
        if (saved === pick) return;
        const q = questions.find((x) => x.questionKey === key);
        if (q) updates.push({ questionId: q.id, response: pick });
      });
      if (updates.length > 0) await strategyApi.saveResponses(locationId, updates);
    },
  });

  // ── Confirm mutation ───────────────────────────────────────────────────────

  const { mutate: confirmSession, isPending: isConfirming } = useMutation({
    mutationFn: async () => {
      await new Promise<void>((resolve, reject) =>
        savePicks(undefined, { onSuccess: resolve, onError: reject }),
      );
      return strategyApi.confirmVapiSession(locationId, session?.id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: strategyKeys.vapiSession(locationId) });
      void queryClient.invalidateQueries({ queryKey: strategyKeys.responses(locationId) });
      navigate('/strategy/builder/profile?section=brand', { replace: true });
    },
    onError: (err: unknown) => showToast((err as Error).message ?? 'Failed to confirm', 'error'),
  });

  // ── Derived state ──────────────────────────────────────────────────────────

  const missingCount = useMemo(
    () => brandQuestions.filter(({ questionKey: key }) => fieldStatus[key] !== 'captured').length,
    [fieldStatus],
  );

  const canConfirm = useMemo(() => {
    // For complete capture: always can confirm
    if (captureLevel === 'complete') return true;
    // For partial: require missing fields to be filled
    return brandQuestions.every(({ questionKey: key }, i) => {
      const status = fieldStatus[key];
      if (status === 'captured') return true;
      return picks[i] !== null;
    });
  }, [captureLevel, fieldStatus, picks]);

  function togglePick(qIdx: number, optIdx: 0 | 1) {
    setPicks((prev) => {
      const next = [...prev] as Pick[];
      next[qIdx] = optIdx;
      return next;
    });
  }

  // ── Loading ────────────────────────────────────────────────────────────────

  const isLoading = confirmToken ? tokenQuery.isLoading : sessionQuery.isLoading;
  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.loading}>Loading your captured answers…</div>
      </div>
    );
  }

  // ── Token expired ──────────────────────────────────────────────────────────

  if (tokenExpired) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.expiredCard}>
          <p className={styles.expiredText}>
            This link has expired. Request a new one and we'll email it to you right away.
          </p>
          <Button onClick={() => resendEmail()} disabled={isResending}>
            {isResending ? 'Sending…' : 'Request a new link'}
          </Button>
        </div>
      </div>
    );
  }

  // ── No session ─────────────────────────────────────────────────────────────

  if (!session) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.expiredCard}>
          <p className={styles.expiredText}>
            {confirmToken && tokenQuery.isError
              ? 'This link has already been used or is no longer valid. Your answers are in the brand step.'
              : 'No call session found. Head back to the brand step to fill in your answers.'}
          </p>
          <Button onClick={() => navigate('/strategy/builder/profile?section=brand')}>
            Go to brand step
          </Button>
        </div>
      </div>
    );
  }

  // ── Already confirmed ──────────────────────────────────────────────────────

  if (session.confirmedAt) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.confirmedBanner}>
          <CheckCircle size={16} color="var(--success)" aria-hidden />
          <span>
            These answers are already confirmed. You can edit them any time in the brand step.
          </span>
        </div>
        <Button onClick={() => navigate('/strategy/builder/profile?section=brand')}>
          Back to brand step
        </Button>
      </div>
    );
  }

  // ── Main confirm screen ────────────────────────────────────────────────────

  return (
    <div className={styles.wrapper}>
      <SectionLabel
        label={
          captureLevel === 'partial' ? 'Finish up your brand answers' : 'Confirm your brand answers'
        }
        sub={
          captureLevel === 'partial'
            ? `We got most of it — ${missingCount} question${missingCount !== 1 ? 's' : ''} still need${missingCount === 1 ? 's' : ''} you.`
            : 'Give these a quick look before we lock them in.'
        }
      />

      {brandQuestions.map(({ questionKey, q, opts }, i) => {
        const status = fieldStatus[questionKey];
        const isMissing = status === 'unclear' || status === 'not_reached';
        const currentPick = picks[i];

        return (
          <Card key={questionKey}>
            <div className={styles.questionHeader}>
              <span className={styles.questionText}>{q}</span>
              {isMissing && <span className={styles.missingBadge}>Needs your input</span>}
            </div>
            <div className={styles.optionGrid}>
              {(opts as readonly string[]).map((opt, j) => (
                <button
                  key={j}
                  className={
                    currentPick === j
                      ? `${styles.optionBtn} ${styles.optionSelected}`
                      : styles.optionBtn
                  }
                  onClick={() => togglePick(i, j as 0 | 1)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </Card>
        );
      })}

      {captureLevel === 'partial' && (
        <div className={styles.reAskRow}>
          <Phone size={14} strokeWidth={1.75} color="var(--text-muted)" aria-hidden />
          <span className={styles.reAskText}>
            Prefer to fill these in over the phone?{' '}
            <button
              className={styles.reAskLink}
              onClick={() => navigate('/strategy/builder/profile?section=brand')}
            >
              Go back and schedule a gap-fill call
            </button>
          </span>
        </div>
      )}

      <div className={styles.actions}>
        <Button
          variant="primary"
          onClick={() => confirmSession()}
          disabled={!canConfirm || isConfirming || isSaving}
        >
          <CheckCircle size={14} strokeWidth={2} aria-hidden />
          {isConfirming || isSaving ? 'Confirming…' : 'Confirm answers'}
        </Button>
        <Button onClick={() => navigate('/strategy/builder/profile?section=brand')}>
          Edit more in the builder
        </Button>
      </div>
    </div>
  );
}
